import React, { useState, useEffect } from 'react';
import { Button, Drawer, Descriptions, Input, Space, Alert, Tag } from 'antd';
import AntTable from '../../components/CustomTable/AntTable';
import { getPendingFraudActions, getPendingFraudActionDetail, approveFraudAction, rejectFraudAction } from '../../services/fraudCheckService';
import { NotificationManager } from 'react-notifications';
import Swal from 'sweetalert2';
import dayjs from 'dayjs';

const { Search } = Input;

const PendingActionsTab = () => {
  const [loading, setLoading] = useState(false);
  const [actions, setActions] = useState([]);
  const [filteredActions, setFilteredActions] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState(null);

  // Drawer for Detail View
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Pagination state
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const fetchPending = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getPendingFraudActions();
      const list = res?.data?.content || res?.data || res?.content || (Array.isArray(res) ? res : []);
      
      // Inject standard 'id' for AntTable's rowKey
      const formatted = list.map((item, index) => ({
        ...item,
        id: item.id || item.reference || `pending_${index}`,
      }));

      setActions(formatted);
      applyFilter(formatted, searchQuery);
    } catch (err) {
      setError(err.message || 'Failed to fetch pending fraud actions.');
    } finally {
      setLoading(false);
    }
  };

  const applyFilter = (list, query) => {
    const q = query.trim().toLowerCase();
    if (!q) {
      setFilteredActions(list);
      setPagination(prev => ({ ...prev, total: list.length }));
      return;
    }
    const filtered = list.filter(item => 
      (item.reference && item.reference.toLowerCase().includes(q)) ||
      (item.account && item.account.toLowerCase().includes(q)) ||
      (item.actionType && item.actionType.toLowerCase().includes(q)) ||
      (item.makerEmail && item.makerEmail.toLowerCase().includes(q))
    );
    setFilteredActions(filtered);
    setPagination(prev => ({ ...prev, total: filtered.length }));
  };

  useEffect(() => {
    fetchPending();
  }, []);

  const handleSearch = (val) => {
    setSearchQuery(val);
    applyFilter(actions, val);
  };

  const handleTableChange = ({ pagination: newPagination }) => {
    setPagination(newPagination);
  };

  const handleInspect = async (record) => {
    setDetailLoading(true);
    setDrawerVisible(true);
    try {
      // Fetch details using the reference if available
      if (record.reference) {
        const detailRes = await getPendingFraudActionDetail(record.reference);
        if (detailRes && (detailRes.data || detailRes.success)) {
          setSelectedAction(detailRes.data || detailRes);
        } else {
          setSelectedAction(record);
        }
      } else {
        setSelectedAction(record);
      }
    } catch (err) {
      console.error('Error fetching pending action details, falling back to table record:', err);
      setSelectedAction(record);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleApprove = (record) => {
    Swal.fire({
      title: 'Approve Fraud Action',
      text: `Are you sure you want to approve this action for reference: ${record.reference}?`,
      icon: 'question',
      input: 'textarea',
      inputPlaceholder: 'Enter approval comments here (required)...',
      inputAttributes: {
        'aria-label': 'Enter approval comments here'
      },
      showCancelButton: true,
      confirmButtonColor: '#28a745',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, Approve',
      preConfirm: (inputValue) => {
        if (!inputValue || !inputValue.trim()) {
          Swal.showValidationMessage('Approval comment is required');
        }
        return inputValue;
      }
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await approveFraudAction(record.reference, result.value);
          if (res && (res.success || res.status === true)) {
            NotificationManager.success(res.message || 'Action approved successfully');
            setDrawerVisible(false);
            fetchPending();
          } else {
            NotificationManager.error(res?.message || 'Failed to approve action');
          }
        } catch (err) {
          NotificationManager.error(err.message || 'An error occurred during approval');
        }
      }
    });
  };

  const handleReject = (record) => {
    Swal.fire({
      title: 'Reject Fraud Action',
      text: `Are you sure you want to reject this action for reference: ${record.reference}?`,
      icon: 'warning',
      input: 'textarea',
      inputPlaceholder: 'Enter rejection reason here (required)...',
      inputAttributes: {
        'aria-label': 'Enter rejection reason here'
      },
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, Reject',
      preConfirm: (inputValue) => {
        if (!inputValue || !inputValue.trim()) {
          Swal.showValidationMessage('Rejection reason/comment is required');
        }
        return inputValue;
      }
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await rejectFraudAction(record.reference, result.value);
          if (res && (res.success || res.status === true)) {
            NotificationManager.success(res.message || 'Action rejected successfully');
            setDrawerVisible(false);
            fetchPending();
          } else {
            NotificationManager.error(res?.message || 'Failed to reject action');
          }
        } catch (err) {
          NotificationManager.error(err.message || 'An error occurred during rejection');
        }
      }
    });
  };

  const columns = [
    {
      title: 'Reference',
      dataIndex: 'reference',
      key: 'reference',
      render: (text) => <strong>{text || 'N/A'}</strong>,
    },
    {
      title: 'Action Type',
      dataIndex: 'actionType',
      key: 'actionType',
      render: (text) => (
        <Tag color={text === 'BLOCK' ? 'red' : 'green'} className="tag-action">
          {text || 'N/A'}
        </Tag>
      ),
    },
    {
      title: 'Account / Target',
      dataIndex: 'account',
      key: 'account',
    },
    {
      title: 'Initiated By',
      dataIndex: 'makerEmail',
      key: 'makerEmail',
    },
    {
      title: 'Date Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => (date ? dayjs(date).format('YYYY-MM-DD HH:mm') : 'N/A'),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: () => <Tag color="warning">PENDING</Tag>,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="middle">
          <Button type="default" size="small" onClick={() => handleInspect(record)}>
            Inspect
          </Button>
          <Button type="primary" size="small" style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }} onClick={() => handleApprove(record)}>
            Approve
          </Button>
          <Button type="primary" danger size="small" onClick={() => handleReject(record)}>
            Reject
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />}

      <div className="fraud-toolbar">
        <Search
          placeholder="Search pending actions by reference or account..."
          allowClear
          onSearch={handleSearch}
          onChange={(e) => handleSearch(e.target.value)}
          style={{ width: 400 }}
        />
        <Button type="default" onClick={fetchPending} loading={loading}>
          Refresh
        </Button>
      </div>

      <AntTable
        loading={loading}
        users={filteredActions}
        pagination={pagination}
        handleGetNext={handleTableChange}
        columns={columns}
      />

      <Drawer
        title={`Pending Action Details: ${selectedAction?.reference || ''}`}
        placement="right"
        width={650}
        onClose={() => setDrawerVisible(false)}
        visible={drawerVisible}
        className="fraud-detail-drawer"
        extra={
          selectedAction && (
            <Space>
              <Button type="primary" style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }} onClick={() => handleApprove(selectedAction)}>
                Approve
              </Button>
              <Button type="primary" danger onClick={() => handleReject(selectedAction)}>
                Reject
              </Button>
            </Space>
          )
        }
      >
        {detailLoading ? (
          <div>Loading details...</div>
        ) : selectedAction ? (
          <div className="fraud-detail-container">
            <div className="fraud-detail-section">
              <h4>Request Information</h4>
              <Descriptions bordered column={1} size="small">
                <Descriptions.Item label="Reference">{selectedAction.reference}</Descriptions.Item>
                <Descriptions.Item label="Action Type">
                  <Tag color={selectedAction.actionType === 'BLOCK' ? 'red' : 'green'}>
                    {selectedAction.actionType}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Status">
                  <Tag color="warning">{selectedAction.status || 'PENDING'}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Creator Email">{selectedAction.makerEmail || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Initiated At">
                  {selectedAction.createdAt ? dayjs(selectedAction.createdAt).format('YYYY-MM-DD HH:mm:ss') : 'N/A'}
                </Descriptions.Item>
              </Descriptions>
            </div>

            <div className="fraud-detail-section">
              <h4>Target & Context Details</h4>
              <Descriptions bordered column={1} size="small">
                <Descriptions.Item label="Account / Target">{selectedAction.account || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Reason / Notes">{selectedAction.reason || 'N/A'}</Descriptions.Item>
                {selectedAction.comment && (
                  <Descriptions.Item label="Maker Comments">{selectedAction.comment}</Descriptions.Item>
                )}
                {selectedAction.metadata && (
                  <Descriptions.Item label="Additional Metadata">
                    <pre style={{ fontSize: '11px', margin: 0 }}>
                      {typeof selectedAction.metadata === 'object' 
                        ? JSON.stringify(selectedAction.metadata, null, 2) 
                        : String(selectedAction.metadata)}
                    </pre>
                  </Descriptions.Item>
                )}
              </Descriptions>
            </div>
          </div>
        ) : (
          <div>No record selected</div>
        )}
      </Drawer>
    </div>
  );
};

export default PendingActionsTab;
